import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import ko from "knockout";
import AppConfig from "AppConfig";
import "./ReportRenderer.css";
import { ActionId, TimeOut as ViewerTimeOut } from 'devexpress-reporting/dx-webdocumentviewer';
import { useFlags } from "launchdarkly-react-client-sdk";
import useManagedAuth from "../../hooks/useManagedAuth";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useSelector, shallowEqual } from "react-redux";
import { USER_TYPES } from "../../constants/index";
import { ajaxSetup } from "@devexpress/analytics-core/analytics-utils";
import { useHistory } from 'react-router-dom';
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import apiClient from "../../auth/apiClient";
import ThemeProvider from "../../providers/ThemeProvider";
import ReportQueueService from "../../services/ReportQueueService";
import ReportQueueControlsWrapper from "./ReportQueueControlsWrapper";
import ReportV2RoutingWrapper from "./ReportV2RoutingWrapper";

const ReportRenderer = ({ className, reportName, reportQueueEnabled, reportQueueV2Enabled }) => {
  const { reportDateRangeLimit } = useFlags();
  const thresholdDays = (reportDateRangeLimit > 0) ? reportDateRangeLimit : 7;
  const viewerRef = useRef();
  const { token, refreshToken } = useManagedAuth();
  const tokenRef = useRef(token);
  const { facilityID } = useCurrentFacility();
  const facilityIDs = useSelector((state) => state.entityScope.available) ?? [];
  const [ajaxInitialized, setAjaxInitialized] = useState(false);
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const facilityGroupID = useSelector((state) => state.entityScope?.facilityGroupId);
  const enqueueSnackbar = useEnqueueSnackbar();
  const history = useHistory();
  const reportQueueService = useMemo(() => new ReportQueueService(apiClient), []);
  const observerRef = useRef(null);
  const [routingDecision, setRoutingDecision] = useState(null);

  const handleRoutingDecisionChange = useCallback((decision) => {
    setRoutingDecision(decision);
  }, []);

  const getReportUri = () => {
    let pairs = {};
    switch (reportName?.toLowerCase()) {
      case "validationstatement":
      case "onepassactivepasses":
        if (currentUser.UserType === USER_TYPES.ValidationAccount)
          pairs["userID"] = currentUser.UserID;
    }
    // this will either be all facilities in group OR a single facility ID:
    if(facilityGroupID && facilityIDs.length > 0) {
      pairs["facilityID"] = facilityIDs.map((f)=>f.id).join();
    } else {
      pairs["facilityID"] = facilityID;
    }
    pairs["facilityGroupID"] = facilityGroupID;
    pairs["currentFacilityID"] = facilityID;

    const params = new URLSearchParams(pairs);
    return encodeURI(`${reportName}?${params.toString()}`);
  };

  const renderControls = useCallback(() => {
    if (observerRef.current) return;

    const observer = new MutationObserver(() => {
      const resetButton = document.querySelector('.dxrdp-parameters-reset');
      const allScrollableContents = document.querySelectorAll('.dx-scrollable-content');
      if (!resetButton) return;

      let targetScrollableContent = null;

      for (const content of allScrollableContents) {
        if (content.contains(resetButton)) {
          targetScrollableContent = content;
          break;
        }
      }

      if (!targetScrollableContent) return;

      if (reportQueueV2Enabled) {
        const alreadyInjected = targetScrollableContent.querySelector('#react-v2-routing-controls');
        if (alreadyInjected) return;

        const reactContainer = document.createElement("div");
        reactContainer.id = "react-v2-routing-controls";
        reactContainer.className = "report-queue-button-container";

        ReactDOM.render(
          <ThemeProvider>
            <ReportV2RoutingWrapper
              reportName={reportName}
              currentUser={currentUser}
              reportQueueService={reportQueueService}
              enqueueSnackbar={enqueueSnackbar}
              history={history}
              thresholdDays={thresholdDays}
              onRoutingDecisionChange={handleRoutingDecisionChange}
            />
          </ThemeProvider>,
          reactContainer
        );

        targetScrollableContent.appendChild(reactContainer);
      } else {
        const alreadyInjected = targetScrollableContent.querySelector('#react-queue-controls');
        if (alreadyInjected) return;

        const reactContainer = document.createElement("div");
        reactContainer.id = "react-queue-controls";
        reactContainer.className = "report-queue-button-container";

        ReactDOM.render(
          <ThemeProvider>
            <ReportQueueControlsWrapper
              reportName={reportName}
              currentUser={currentUser}
              reportQueueService={reportQueueService}
              enqueueSnackbar={enqueueSnackbar}
              history={history}
            />
          </ThemeProvider>,
          reactContainer
        );

        targetScrollableContent.appendChild(reactContainer);
      }

      observer.disconnect();
      observerRef.current = null;
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    observerRef.current = observer;
  }, [reportName, currentUser, reportQueueService, enqueueSnackbar, history, reportQueueV2Enabled, thresholdDays, handleRoutingDecisionChange]);

  useEffect(() => {
    if (reportQueueEnabled || reportQueueV2Enabled) {
      renderControls();
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [renderControls, reportQueueEnabled, reportQueueV2Enabled]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (!reportName || !ajaxInitialized || !token) {
      return;
    }

    if (reportQueueV2Enabled) {
      // V2: standard toolbar visible, ReportV2RoutingWrapper manages Submit visibility
      ko.applyBindings(
        {
          reportUrl: getReportUri(),
          requestOptions: {
            host: `${AppConfig.externalServices.baseURL}/reports/`,
            invokeAction: "DXXRDV",
          },
          callbacks: {
            BeforeRender: function(s, e) {
              ViewerTimeOut(AppConfig.reportViewerTimeoutMs ?? 600000);
            },
            OnExport: function(src, event) {
              event.FormData["access_token"] = tokenRef.current;
              event.RequestUrl = `${AppConfig.externalServices.baseURL}/reports/DXXRDV/export`;
            },
            CustomizeMenuActions: function(src, event) {
              var actionPrint = event.GetById(ActionId.Print);
              if (actionPrint) actionPrint.visible = false;

              var actionPrintPage = event.GetById(ActionId.PrintPage);
              if (actionPrintPage) actionPrintPage.visible = false;
            },
            CustomizeElements: function (s, e) {
              renderControls();
            }
          },
        },
        viewerRef.current
      );

      return () => ko.cleanNode(viewerRef.current);
    } else if (reportQueueEnabled) {
      ko.applyBindings(
        {
          reportUrl: getReportUri(),
          requestOptions: {
            host: `${AppConfig.externalServices.baseURL}/reports/`,
            invokeAction: "DXXRDV",
          },
          callbacks: {
            BeforeRender: function(s, e) {
              ViewerTimeOut(AppConfig.reportViewerTimeoutMs ?? 600000);
            },
            CustomizeElements: function (s, e) {
              renderControls();
            }
          },
        },
        viewerRef.current
      );

      return () => ko.cleanNode(viewerRef.current);
    } else {
      ko.applyBindings(
        {
          reportUrl: getReportUri(),
          requestOptions: {
            host: `${AppConfig.externalServices.baseURL}/reports/`,
            invokeAction: "DXXRDV",
          },
          callbacks: {
            BeforeRender: function(s, e) {
              ViewerTimeOut(AppConfig.reportViewerTimeoutMs ?? 600000);
            },
            OnExport: function(src, event) {
              event.FormData["access_token"] = tokenRef.current;
              event.RequestUrl = `${AppConfig.externalServices.baseURL}/reports/DXXRDV/export`;
            },
            CustomizeMenuActions: function(src, event) {
              // Get the page navigation actions and hide them.
              var actionPrint = event.GetById(
                ActionId.Print
              );
              if (actionPrint) actionPrint.visible = false;

              var actionPrintPage = event.GetById(
                ActionId.PrintPage
              );

              if (actionPrintPage) actionPrintPage.visible = false;
            },
          },
        },
        viewerRef.current
      );

      return () => ko.cleanNode(viewerRef.current);
    }
  }, [reportName, ajaxInitialized, reportQueueEnabled, reportQueueV2Enabled]);

  useEffect(() => {
    if (!token) {
      return;
    }

    ajaxSetup.ajaxSettings = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      error: function(jqXHR, textStatus, errorThrown) {
        if (jqXHR.status == 401) {
          refreshToken();
        }
      },
    };

    setAjaxInitialized(true);
  }, [token]);

  // Scenario 1 – V2 immediate (date range ≤ threshold): toolbar VISIBLE
  // Scenario 2 – V2 queue (date range > threshold or dates missing): toolbar HIDDEN
  // Scenario 3 – V1 only (V2 disabled): toolbar HIDDEN (unchanged)
  // When both V1 and V2 are enabled, V2 takes priority.
  const isImmediateReport = routingDecision === "immediate";
  const hideToolbar = reportQueueV2Enabled
    ? !isImmediateReport
    : reportQueueEnabled;

  // V2 always hides native DevExpress parameter buttons (wrapper provides its own)
  const hideNativeParams = reportQueueV2Enabled;

  return (
    <div className={`${className} ${hideToolbar ? "hide-toolbar" : ""} ${hideNativeParams ? "hide-native-params" : ""}`}>
      <div
        className="viewport"
        ref={viewerRef}
        data-bind="dxReportViewer: $data"
      />
    </div>
  );
};

ReportRenderer.propTypes = {
  className: PropTypes.string,
  reportName: PropTypes.string.isRequired,
  reportQueueEnabled: PropTypes.bool,
  reportQueueV2Enabled: PropTypes.bool,
};

export default ReportRenderer;
